/**
 * Calendar event types and interfaces
 */

export type CalendarEventType = 'appointment' | 'unavailable' | 'availability';

// Predefined colors for practitioners
export const practitionerColors: string[] = [
  '#4caf50',  // Green
  '#2196f3',  // Blue
  '#9c27b0',  // Purple
  '#ff9800',  // Orange
  '#e91e63',  // Pink
  '#3f51b5',  // Indigo
  '#009688',  // Teal
  '#ff5722',  // Deep Orange
  '#673ab7',  // Deep Purple
  '#795548',  // Brown
];

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: CalendarEventType;
  practitionerId: string;
  practitionerName?: string;
  patientId?: string;
  patientName?: string;
  patientEmail?: string;
  patientPhone?: string;
  notes?: string;
  color?: string; // For different types of events
  recurring?: boolean;
  cancelled?: boolean;
  cancellationReason?: number;
  cancellationNote?: string;
  cancelledAt?: Date | null;
  status?: string;
  appointmentTypeId?: string;
  appointmentTypeName?: string;
}

export interface Appointment {
  id: string;
  patient_id?: string;
  patient_name?: string;
  patient_email?: string;
  patient_phone?: string;
  practitioner_id: string;
  practitioner_name?: string;
  appointment_type_id?: string;
  appointment_type_name?: string;
  starts_at: string;
  ends_at: string;
  notes?: string;
  status?: string;
  canceled_at?: string;
  did_not_arrive?: boolean;
  cancellation_note?: string;
  cancellation_reason?: number;
}

// Valid cancellation reasons for Cliniko
export enum CancellationReason {
  FeelingBetter = 1,
  ConditionWorse = 2,
  Sick = 3,
  CovidRelated = 4,
  Away = 5,
  Work = 6,
  Other = 7
}

// String representations of cancellation reasons
export const cancellationReasonLabels: Record<CancellationReason, string> = {
  [CancellationReason.FeelingBetter]: "Feeling Better",
  [CancellationReason.ConditionWorse]: "Condition Worse",
  [CancellationReason.Sick]: "Sick",
  [CancellationReason.CovidRelated]: "COVID-19 related",
  [CancellationReason.Away]: "Away",
  [CancellationReason.Work]: "Work",
  [CancellationReason.Other]: "Other"
};

export interface UnavailableBlock {
  id: string;
  practitioner_id: string;
  practitioner_name?: string;
  starts_at: string;
  ends_at: string;
  notes?: string;
  unavailable_block_type_id?: string;
  unavailable_block_type_name?: string;
  repeat_rule?: {
    number_of_repeats: number;
    repeat_type: string;
    repeating_interval: number;
  };
}

export interface DailyAvailability {
  id: string;
  practitioner_id: string;
  day_of_week: number; // 0 = Sunday, 1 = Monday, etc.
  start_time: string; // "HH:MM" format
  end_time: string; // "HH:MM" format
}

/**
 * Utility functions
 */

// Convert a Cliniko appointment to our unified CalendarEvent format
export const appointmentToCalendarEvent = (appointment: any): CalendarEvent => {
  // Debug the appointment data we receive
  console.log('Converting appointment to calendar event:', appointment);
  
  // Check for cancellation using multiple indicators
  const isCancelled = Boolean(
    appointment.canceled_at || 
    appointment.cancellation_reason || 
    appointment.cancellation_note || 
    appointment.status === 'cancelled'
  );

  // Use practitioner-specific color or default based on appointment status
  let color;
  
  if (isCancelled) {
    color = '#9e9e9e'; // Grey for cancelled appointments
  } else {
    // Generate a color based on practitioner ID
    // Use a hash function to consistently map practitioner IDs to colors
    const practitionerId = appointment.practitioner_id;
    console.log('Appointment practitioner ID:', practitionerId);
    
    if (practitionerId && typeof practitionerId === 'string') {
      const colorIndex = Math.abs(practitionerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % practitionerColors.length;
      color = practitionerColors[colorIndex];
      console.log(`Using color ${color} for practitioner ${practitionerId} (index: ${colorIndex})`);
    } else {
      color = practitionerColors[0];
      console.log(`No valid practitioner ID found, using default color ${color}`);
    }
  }

  return {
    id: appointment.id,
    title: appointment.patient_name || 'Unknown Patient',
    start: new Date(new Date(appointment.starts_at).getTime() + (30 * 60 * 1000)), // Add 30 minutes to fix display
    end: new Date(new Date(appointment.ends_at).getTime() + (30 * 60 * 1000)), // Add 30 minutes to fix display
    patientId: appointment.patient_id,
    patientName: appointment.patient_name,
    patientEmail: appointment.patient_email,
    patientPhone: appointment.patient_phone,
    practitionerId: appointment.practitioner_id,
    practitionerName: appointment.practitioner_name,
    appointmentTypeId: appointment.appointment_type_id,
    appointmentTypeName: appointment.appointment_type_name,
    notes: appointment.notes,
    color: color,
    cancelled: isCancelled,
    cancelledAt: appointment.canceled_at ? new Date(appointment.canceled_at) : null,
    cancellationReason: appointment.cancellation_reason,
    cancellationNote: appointment.cancellation_note,
    status: isCancelled ? 'cancelled' : 'active',
    type: 'appointment'
  };
};

// Convert a Cliniko unavailable block to our unified CalendarEvent format
export const unavailableBlockToCalendarEvent = (block: UnavailableBlock): CalendarEvent => {
  return {
    id: block.id,
    title: 'Unavailable',
    start: new Date(new Date(block.starts_at).getTime() + (30 * 60 * 1000)), // Add 30 minutes to fix display
    end: new Date(new Date(block.ends_at).getTime() + (30 * 60 * 1000)), // Add 30 minutes to fix display
    type: 'unavailable',
    practitionerId: block.practitioner_id,
    practitionerName: block.practitioner_name,
    notes: block.notes,
    color: '#f44336', // Red for unavailable blocks
    recurring: !!block.repeat_rule,
  };
};

// Generate a calendar event from a daily availability for a specific date
export const dailyAvailabilityToCalendarEvent = (
  availability: DailyAvailability,
  date: Date
): CalendarEvent => {
  // Clone the date object to avoid mutating the original
  const startDate = new Date(date);
  const endDate = new Date(date);
  
  // Parse the time strings (HH:MM format)
  const [startHours, startMinutes] = availability.start_time.split(':').map(Number);
  const [endHours, endMinutes] = availability.end_time.split(':').map(Number);
  
  startDate.setHours(startHours, startMinutes, 0, 0);
  endDate.setHours(endHours, endMinutes, 0, 0);
  
  return {
    id: `availability-${availability.id}-${date.toISOString().split('T')[0]}`,
    title: 'Available Hours',
    start: startDate,
    end: endDate,
    type: 'availability',
    practitionerId: availability.practitioner_id,
    color: '#2196f3', // Blue for availability
  };
};